<!-- CSVUploader.vue -->
<template>
    <div class="csv-uploader">
        <div class="upload-container">
            <div
                class="drop-zone"
                @dragover.prevent
                @drop.prevent="handleDrop"
                @click="triggerFileInput"
                :class="{ 'is-dragging': isDragging }"
                @dragenter.prevent="isDragging = true"
                @dragleave.prevent="isDragging = false"
            >
                <input type="file" ref="fileInput" accept=".csv" @change="handleFileSelect" class="hidden-input" />
                <div class="upload-icon">
                    <i class="fas fa-upload"></i>
                </div>
                <p class="upload-text">
                    {{ isDragging ? 'Drop your CSV file here' : 'Click or drag file to upload' }}
                </p>
                <p class="file-type">CSV files only</p>
            </div>
        </div>

        <div class="p-fluid p-formgrid p-grid" style="flex-direction: row; display: flex; justify-content: space-between">
            <div>
                <h5>โปรดเลือกหัวข้อที่ต้องการเพิ่มข้อมูล</h5>

                <Dropdown
                    v-model="selectedTopic"
                    :options="topicOptions_AT"
                    optionLabel="name"
                    :filter="true"
                    placeholder="Select a topic"
                    :showClear="true"
                >
                    <template #value="slotProps">
                        <div class="country-item country-item-value" v-if="slotProps.value">
                            <div>{{ slotProps.value.name }}</div>
                        </div>
                        <span v-else>
                            {{ slotProps.placeholder }}
                        </span>
                    </template>
                    <template #option="slotProps">
                        <div class="country-item">
                            <div>{{ slotProps.option.name }}</div>
                        </div>
                    </template>
                </Dropdown>
            </div>
            <div>
                <h5>ดาวโหลดแบบฟอร์ม CSV</h5>
                <SplitButton label="Download Template" :model="template_items" class="p-button-success table-header"></SplitButton>
            </div>
        </div>
        <!-- Loading State -->
        <div v-if="isLoading" class="loading-state">Processing...</div>

        <!-- Error Message -->
        <div v-if="error" class="error-message">
            {{ error }}
        </div>

        <!-- Preview Table -->
        <div v-if="parsedData.length" class="preview-container">
            <h3>Preview:</h3>
            <div class="table-wrapper" @scroll="handleScroll" style="max-height: 400px; overflow-y: auto">
                <table>
                    <thead>
                        <tr>
                            <th v-for="header in headers" :key="header">{{ header }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(row, index) in previewRows" :key="index">
                            <td v-for="header in headers" :key="header">{{ row[header] }}</td>
                        </tr>
                    </tbody>
                </table>
                <div v-if="isLoadingMore" class="loading-more">Loading more...</div>
            </div>

            <div class="preview-info">Showing {{ previewRows.length }} of {{ parsedData.length }} rows</div>

            <div class="p-col-12 p-md-6" style="text-align: right; flex-direction: row; display: flex; justify-content: space-between">
                <Button @click="uploadCSV = false" :label="$t('cancelBtn')" class="p-button-secondary" />
                <Button @click="handleUpload" :label="$t('addBtn')" :disabled="isUploading" class="p-button-success" />
                <div v-if="uploadStatus" :class="{ error: uploadStatus.startsWith('Error') }">
                    {{ uploadStatus }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Papa from 'papaparse';

export default {
    name: 'CSVUploader',

    data() {
        return {
            template_items: [
                {
                    label: 'ข้อมูลอุปกรณ์ยานพาหนะ',
                    icon: 'pi pi-download',
                    command: () => {
                        this.downloadFile('initial_equipment_1.csv');
                    }
                },
                {
                    label: 'ข้อมูลถังดับเพลิง',
                    icon: 'pi pi-download',
                    command: () => {
                        this.downloadFile('initial_extinguisher_1.csv');
                    }
                },
                {
                    label: 'ข้อมูลหัวจ่ายน้ำดับเพลิง',
                    icon: 'pi pi-download',
                    command: () => {
                        this.downloadFile('initial_firehydrant_1.csv');
                    }
                },
                {
                    label: 'ข้อมูลสถานประกอบการ',
                    icon: 'pi pi-download',
                    command: () => {
                        this.downloadFile('public_org_business_detail_1.csv');
                    }
                },
                {
                    label: 'ข้อมูลกำลังคน',
                    icon: 'pi pi-download',
                    command: () => {
                        this.downloadFile('initial_officer_1.csv');
                    }
                },
                {
                    label: 'ข้อมูลการใช้งานระบบ',
                    icon: 'pi pi-download',
                    command: () => {
                        this.downloadFile('action_logs_1.csv');
                    }
                }
            ],

            topicOptions_AT: [
                { name: 'ข้อมูลอุปกรณ์ยานพาหนะ', topic: 'equipment' },
                { name: 'ข้อมูลถังดับเพลิง', topic: 'extinguisher' },
                { name: 'ข้อมูลหัวจ่ายน้ำดับเพลิง', topic: 'firehydrant' },
                { name: 'ข้อมูลสถานประกอบการ', topic: 'publicOrganization' },
                { name: 'ข้อมูลกำลังคน', topic: 'officer' },
                { name: 'ข้อมูลการใช้งานระบบ', topic: 'action_logs' }
            ],

            selectedTopic: null,
            isUploading: false,
            uploadStatus: '',
            visibleRows: 5,
            isLoadingMore: false,
            uploadCSV: false,
            parsedData: [],
            headers: [],
            error: '',
            isLoading: false,
            isDragging: false
        };
    },

    computed: {
        previewRows() {
            return this.parsedData.slice(0, this.visibleRows);
        }
    },

    methods: {
        async downloadFile(filename) {
            try {
                const response = await fetch(`${this.$url_service}/api/download-template/${filename}`, {
                    method: 'GET',
                    headers: {
                        Accept: 'text/csv'
                    }
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                // Get the text content
                const text = await response.text();

                // Create blob with CSV MIME type
                const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
                const url = window.URL.createObjectURL(blob);

                // Create and trigger download link
                const link = document.createElement('a');
                link.style.display = 'none';
                link.href = url;
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();

                // Cleanup
                setTimeout(() => {
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                }, 100);

                this.$toast.add({
                    severity: 'success',
                    detail: 'File downloaded successfully',
                    life: 3000
                });
            } catch (error) {
                console.error('Download failed:', error);
                this.$toast.add({
                    severity: 'error',
                    detail: 'Download failed: ' + error.message,
                    life: 3000
                });
            }
        },

        validateData(data) {
            if (this.selectedTopic.topic === 'action_logs') {
                const requiredFields = ['user_id', 'action_time', 'detail', 'topic'];

                if (!Array.isArray(data) || data.length === 0) {
                    throw new Error('CSV file is empty or invalid format');
                }

                const invalidRows = data.reduce((errors, row, index) => {
                    const missingFields = requiredFields.filter((field) => !row[field] || row[field].toString().trim() === '');

                    if (missingFields.length > 0) {
                        errors.push(`Row ${index + 1}: Missing ${missingFields.join(', ')}`);
                    }
                    return errors;
                }, []);

                if (invalidRows.length > 0) {
                    throw new Error(`Data validation failed:\n${invalidRows.join('\n')}`);
                }

                return true;
            }

            if (this.selectedTopic.topic === 'extinguisher') {
                // console.log('Validating extinguisher data', data[0]);

                const requiredFields = [
                    'fx_id',
                    'status',
                    'fire_extinguisher_code',
                    'place_name',
                    'country',
                    'subdistrict',
                    'fire_extinguisher_size',
                    'type',
                    'latch_lock',
                    'joystick',
                    'pressure_gauge',
                    'line_spray',
                    'chassis',
                    'lat',
                    'lon',
                    'location_type',
                    'date_created',
                    'fire_station',
                    'flag',
                    'details',
                    'image_cloud',
                    'province',
                    'date_update',
                    'user_organize',
                    'main_organize',
                    'date_expire',
                    'code',
                    'system_id',
                    'extinguisher_status'
                ];

                if (!Array.isArray(data) || data.length === 0) {
                    throw new Error('CSV file is empty or invalid format');
                }

                return true;
            }

            if (this.selectedTopic.topic === 'firehydrant') {
                // console.log('Validating extinguisher data', data[0]);

                const requiredFields = [
                    'firehydrant_id',
                    'location_id',
                    'event_lat',
                    'event_lon',
                    'event_detail',
                    'flag',
                    'pipe_size',
                    'fh_province_name',
                    'fh_amphur_name',
                    'fh_tambon_name',
                    'road_name',
                    'user_organize',
                    'fire_station',
                    'soi_name',
                    'community_name',
                    'status',
                    'inspec_details',
                    'date_create',
                    'date_update',
                    'image',
                    'location',
                    'image_cloud',
                    'fs_id',
                    'code'
                ];

                if (!Array.isArray(data) || data.length === 0) {
                    throw new Error('CSV file is empty or invalid format');
                }

                return true;
            }

            if (this.selectedTopic.topic === 'equipment') {
                // console.log('Validating extinguisher data', data[0]);

                const requiredFields = [
                    'ev_id',
                    'ev_name',
                    'ev_organize',
                    'ev_image',
                    'ev_group',
                    'ev_type',
                    'ev_number',
                    'ev_status',
                    'ev_license_plate',
                    'ev_sidecar_number',
                    'ev_date',
                    'ev_response_name',
                    'ev_response_position',
                    'ev_response_phone',
                    'ev_detail',
                    'ev_maintenance_table',
                    'ev_vehicledevice_table',
                    'remind_check',
                    'flag',
                    'editor',
                    'user_organize',
                    'ev_standard_name',
                    'ev_amount',
                    'ev_borrowing_history',
                    'tel_detail',
                    'ev_maintenance_table2',
                    'date_create',
                    'date_update',
                    'ev_car_brand',
                    'ev_received_date',
                    'image_cloud',
                    'ev_lat',
                    'ev_lon',
                    'province',
                    'code',
                    'equipment_community'
                ];

                if (!Array.isArray(data) || data.length === 0) {
                    throw new Error('CSV file is empty or invalid format');
                }

                // Only check if field exists, allow empty values
                // const invalidRows = data.reduce((errors, row, index) => {
                //     const missingFields = requiredFields.filter((field) => !row[field] || row[field].toString().trim() === '');

                //     if (missingFields.length > 0) {
                //         errors.push(`Row ${index + 1}: Missing ${missingFields.join(', ')}`);
                //     }
                //     return errors;
                // }, []);

                // if (invalidRows.length > 0) {
                //     throw new Error(`Data validation failed:\n${invalidRows.join('\n')}`);
                // }

                return true;
            }

            if (this.selectedTopic.topic === 'publicOrganization') {
                // console.log('Validating extinguisher data', data[0]);

                const requiredFields = [
                    'org_owner_name',
                    'org_name',
                    'organize_contact_phone',
                    'organize_contact_mobile',
                    'organize_coordinator_name',
                    'org_lat',
                    'org_lon',
                    'hsp_license_id',
                    'hsp_type',
                    'hsp_license_sequence',
                    'factory_license_id',
                    'factory_detail',
                    'factory_license_sequence',
                    'org_business_condition',
                    'activity_name',
                    'org_business_status',
                    'remark',
                    'risk_level',
                    'date_create',
                    'date_update',
                    'fs_tname',
                    'response_organize',
                    'org_location',
                    'org_tambon',
                    'org_amphur',
                    'org_province',
                    'org_id',
                    'business_id',
                    'org_postcode',
                    'hsp_expire_date'
                ];

                if (!Array.isArray(data) || data.length === 0) {
                    throw new Error('CSV file is empty or invalid format');
                }

                return true;
            }

            if (this.selectedTopic.topic === 'officer') {
                // console.log('Validating extinguisher data', data[0]);

                const requiredFields = [
                    'officer_id',
                    'officer_prefix',
                    'officer_firstname',
                    'officer_lastname',
                    'officer_organize',
                    'officer_image',
                    'officer_level_id',
                    'officer_position',
                    'officer_startwork',
                    'officer_phone',
                    'officer_mobile_phone',
                    'officer_fax',
                    'officer_email',
                    'officer_radio_signal',
                    'officer_response_signal',
                    'secretary_name',
                    'secretary_phone',
                    'update_date',
                    'officer_detail',
                    'specialist_id',
                    'disaster_id',
                    'training_history_table',
                    'flag',
                    'editor',
                    'update_info',
                    'user_organize',
                    'tel_detail',
                    'date_create',
                    'date_update',
                    'officer_birthdate',
                    'officer_id_card',
                    'created_by',
                    'updated_by'
                ];

                if (!Array.isArray(data) || data.length === 0) {
                    throw new Error('CSV file is empty or invalid format');
                }

                return true;
            }
        },

        async handleUpload() {
            try {
                console.log('Starting upload for:', this.selectedTopic.topic);
                console.log('First row sample:', this.parsedData[0]);

                this.isUploading = true;
                this.uploadStatus = 'Validating data...';

                this.validateData(this.parsedData);

                this.uploadStatus = 'Uploading...';
                const response = await fetch(`${this.$url_service}/api/upload-csv`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        data: this.parsedData,
                        topic: this.selectedTopic.topic
                    })
                });

                const responseData = await response.json();
                console.log('Server response:', responseData);

                if (!response.ok) {
                    throw new Error(responseData.message || 'Upload failed');
                }

                this.uploadStatus = 'Upload successful!';
                this.$emit('upload-complete', responseData);
                this.uploadCSV = false;
            } catch (error) {
                console.error('Upload error details:', error);
                this.uploadStatus = `Error: ${error.message}`;
            } finally {
                this.isUploading = false;
            }
        },

        handleScroll(event) {
            const element = event.target;
            if (
                element.scrollHeight - element.scrollTop <= element.clientHeight + 100 &&
                !this.isLoadingMore &&
                this.visibleRows < this.parsedData.length
            ) {
                this.loadMore();
            }
        },
        loadMore() {
            this.isLoadingMore = true;
            setTimeout(() => {
                this.visibleRows += 5;
                this.isLoadingMore = false;
            }, 200);
        },

        triggerFileInput() {
            this.$refs.fileInput.click();
        },

        handleDrop(event) {
            this.isDragging = false;
            const file = event.dataTransfer.files[0];
            this.processFile(file);
        },

        handleFileSelect(event) {
            const file = event.target.files[0];
            this.processFile(file);
        },

        async processFile(file) {
            if (!file) return;

            // Reset states
            this.error = '';
            this.parsedData = [];
            this.headers = [];

            // Validate file type
            if (!file.name.endsWith('.csv')) {
                this.error = 'Please upload a CSV file';
                return;
            }

            this.isLoading = true;

            try {
                Papa.parse(file, {
                    header: true,
                    skipEmptyLines: true,
                    dynamicTyping: true,
                    complete: this.handleParseComplete,
                    error: this.handleParseError
                });
            } catch (error) {
                this.handleParseError(error);
            }
        },

        async handleParseComplete(results) {
            if (results.errors.length > 0) {
                this.handleParseError(results.errors[0]);
                return;
            }

            try {
                this.parsedData = results.data;
                this.headers = Object.keys(results.data[0] || {});

                // Emit the parsed data to parent component
                this.$emit('upload-complete', results.data);
            } catch (error) {
                this.error = 'Failed to upload to database';
            } finally {
                this.isLoading = false;
            }
        },

        handleParseError(error) {
            this.error = 'Error processing CSV file';
            this.isLoading = false;
            console.error('CSV Parse Error:', error);
        }
    }
};
</script>

<style scoped>
.csv-uploader {
    max-width: 800px;
    margin: 0 auto;
    padding: 20px;
}

.upload-container {
    margin-bottom: 20px;
}

.drop-zone {
    border: 2px dashed #ccc;
    border-radius: 8px;
    padding: 40px;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s ease;
}

.drop-zone:hover {
    background-color: #f8f9fa;
}

.is-dragging {
    background-color: #f8f9fa;
    border-color: #007bff;
}

.hidden-input {
    display: none;
}

.upload-icon {
    font-size: 48px;
    color: #6c757d;
    margin-bottom: 15px;
}

.upload-text {
    font-size: 16px;
    color: #495057;
    margin-bottom: 5px;
}

.file-type {
    font-size: 14px;
    color: #6c757d;
}

.loading-state {
    text-align: center;
    padding: 20px;
    color: #6c757d;
}

.error-message {
    background-color: #f8d7da;
    color: #721c24;
    padding: 12px;
    border-radius: 4px;
    margin-bottom: 20px;
}

.preview-container {
    margin-top: 20px;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 20px;
}

.preview-container h3 {
    margin-bottom: 15px;
    font-size: 18px;
}

.table-wrapper {
    overflow-x: auto;
}

table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 10px;
}

th,
td {
    padding: 12px;
    border: 1px solid #dee2e6;
    text-align: left;
}

th {
    background-color: #f8f9fa;
    font-weight: 600;
}

.preview-info {
    text-align: right;
    color: #6c757d;
    font-size: 14px;
}

.p-splitbutton {
    margin-right: 0.5rem;
}
</style>
